import { useMutation, useQuery } from '@apollo/client/react';
import { Avatar, Badge, Box, Button, Card, Center, Heading, HStack, Separator, SimpleGrid, Spinner, Text, VStack, Wrap } from '@chakra-ui/react';
import { advisorsBreadcrumbsWithTrail, useAdvisorsToolbar } from '@src/components/sco/Advisors/hooks/useAdvisorsToolbar';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { PaywalledAdvisorAction } from '@src/components/sco/Advisors/shared/PaywalledAdvisorAction';
import { SectionScroll } from '@src/components/sco/Advisors/shared/SectionScroll';
import { SpecialtyTag } from '@src/components/sco/Advisors/shared/SpecialtyTag';
import { categoryMetaFromMatch, commitmentLabelsFromMatch, companyFromMatch, compensationLabelsFromMatch, founderDisplayName, founderFromMatch, founderProfileHref, hasAppliedToSeat, isPublishedAdvisorOpenSeat, seatApplicationState, matchFactorTitles, matchPercentLabel, seatTitleFromMatch, skillTitlesFromMatch, whyTextFromMatch, type OpenSeatMatch } from '@src/components/sco/Advisors/utils/openSeatMatches';
import { AutoResizeTextarea } from '@src/components/sco/AutoResizeTextarea';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { APPLY_TO_BOARD_SEAT } from '@src/gql/mutations/ApplyToBoardSeat';
import { ADVISOR_OPEN_SEAT_MATCH } from '@src/gql/queries/AdvisorMatchedOpenSeats';
import { ADVISOR_OPPORTUNITY_COUNTS, ADVISOR_SEAT_APPLICATIONS } from '@src/gql/queries/AdvisorOpportunities';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { useMemo, useState } from 'react';
import { LuArrowLeft, LuCheck, LuSend } from 'react-icons/lu';
type BoardSeatProfileSectionProps = {
  userMatchId?: string;
};
export const BoardSeatProfileSection = ({
  userMatchId
}: BoardSeatProfileSectionProps) => {
  const paths = useAdvisorsPaths();
  const {
    currentUser
  } = useCurrentUser();
  const [showApplyForm, setShowApplyForm] = useState(false);
  const [messageError, setMessageError] = useState<string | null>(null);
  const query = useQuery(ADVISOR_OPEN_SEAT_MATCH, {
    skip: !userMatchId || !currentUser?.userId,
    variables: {
      userMatchId: userMatchId!
    },
    fetchPolicy: 'network-only'
  });
  const match = query.data?.scoUserMatch as OpenSeatMatch | null | undefined;
  const founder = match ? founderFromMatch(match) : null;
  const company = match ? companyFromMatch(match) : null;
  const category = match ? categoryMetaFromMatch(match) : null;
  const skills = match ? skillTitlesFromMatch(match) : [];
  const factors = match ? matchFactorTitles(match, 8) : [];
  const why = match ? whyTextFromMatch(match) : null;
  const pct = match ? matchPercentLabel(match) : null;
  const applied = match ? hasAppliedToSeat(match) : false;
  const applicationState = match ? seatApplicationState(match) : 'none';
  const founderUserId = founder?.userId;
  const inboxHref = applicationState === 'accepted' && founderUserId ? paths.withQuery(paths.inbox, {
    founderUserId
  }) : null;
  const compLabels = match ? compensationLabelsFromMatch(match) : [];
  const commitLabels = match ? commitmentLabelsFromMatch(match) : [];
  const groupRequestId = match?.userSearch?.groupRequest?.groupRequestId;
  const memberHref = match ? founderProfileHref(match) : null;
  const isTargetAdvisor = !!match?.targetUserId && match.targetUserId === currentUser?.userId;
  const isAdvisorSeat = match?.userSearch?.groupRequest?.groupRequestTypeKey === 'advisor' || isPublishedAdvisorOpenSeat(match);
  const toolbarBreadcrumbs = useMemo(() => advisorsBreadcrumbsWithTrail({
    title: 'Opportunities',
    href: paths.opportunities
  }, {
    title: match ? seatTitleFromMatch(match) : 'Open seat',
    href: userMatchId ? `${paths.opportunities}/${encodeURIComponent(userMatchId)}` : paths.opportunities
  }), [match, userMatchId]);
  useAdvisorsToolbar({
    breadcrumbs: toolbarBreadcrumbs
  });
  const [applyToBoardSeat, applyMutation] = useMutation(APPLY_TO_BOARD_SEAT, {
    refetchQueries: [{
      query: ADVISOR_OPPORTUNITY_COUNTS
    }, {
      query: ADVISOR_SEAT_APPLICATIONS,
      variables: {
        pageSize: 20,
        page: 1
      }
    }]
  });
  const [applyMessage, setApplyMessage] = useState('');
  const handleApply = async () => {
    setMessageError(null);
    if (!groupRequestId) {
      setMessageError('This seat is not open for applications.');
      return;
    }
    try {
      await applyToBoardSeat({
        variables: {
          input: {
            groupRequestId,
            message: applyMessage.trim() || undefined
          }
        }
      });
      setShowApplyForm(false);
      setApplyMessage('');
      void query.refetch();
    } catch (err) {
      setMessageError(err instanceof Error ? err.message : 'Could not send your application.');
    }
  };
  if (!currentUser) {
    return <Center height={'full'} padding={8}>
        <Text>Sign in to view this open board seat.</Text>
      </Center>;
  }
  if (query.loading) {
    return <Center height={'full'} padding={8}>
        <Spinner size={'xl'} />
      </Center>;
  }
  if (query.error) {
    return <Center height={'full'} padding={8}>
        <Text color={'red.fg'}>{query.error.message}</Text>
      </Center>;
  }
  if (!match?.userMatchId || !groupRequestId || !isTargetAdvisor || !isAdvisorSeat) {
    return <Center height={'full'} padding={8}>
        <VStack gap={3}>
          <Text>This open seat could not be found, or you are not matched to it.</Text>
          <Button asChild variant={'outline'}>
            <NextLink href={paths.opportunities}>
              <LuArrowLeft />
              Back to opportunities
            </NextLink>
          </Button>
        </VStack>
      </Center>;
  }
  return <SectionScroll>
      <Button asChild alignSelf={'flex-start'} size={'sm'} variant={'ghost'}>
        <NextLink href={paths.opportunities}>
          <LuArrowLeft />
          Opportunities
        </NextLink>
      </Button>

      <VStack align={'stretch'} gap={1}>
        <HStack gap={2} wrap={'wrap'}>
          <Heading size={'2xl'}>{seatTitleFromMatch(match)}</Heading>
          {pct && <Badge colorPalette={'green'} variant={'subtle'}>
              {pct}
            </Badge>}
          {applicationState === 'pending' && <Badge colorPalette={'blue'} variant={'subtle'}>
              Applied
            </Badge>}
          {applicationState === 'accepted' && <Badge colorPalette={'green'} variant={'subtle'}>
              Accepted
            </Badge>}
          {applicationState === 'declined' && <Badge colorPalette={'red'} variant={'subtle'}>
              Declined
            </Badge>}
        </HStack>
        <Text color={'colorPalette.fg/60'} fontSize={'md'}>
          Board seat opportunity · review the founder and offer to help
        </Text>
      </VStack>

      <SimpleGrid alignItems={'start'} columns={{
      base: 1,
      lg: 3
    }} gap={6}>
        <VStack align={'stretch'} gap={4} gridColumn={{
        lg: 'span 2'
      }}>
          <Card.Root variant={'subtle'}>
            <Card.Body gap={4}>
              <HStack align={'center'} gap={4}>
                <Avatar.Root size={'2xl'}>
                  <Avatar.Fallback name={founderDisplayName(match)} />
                  {founder?.pictureUrl ? <Avatar.Image src={founder.pictureUrl} /> : null}
                </Avatar.Root>
                <VStack align={'start'} flex={1} gap={1}>
                  <Heading lineHeight={'shorter'} size={'lg'}>
                    {founderDisplayName(match)}
                  </Heading>
                  <Text color={'colorPalette.fg/60'} fontSize={'sm'}>
                    {[company?.name, founder?.title].filter(Boolean).join(' · ') || 'Founder'}
                  </Text>
                  {founder?.shortBio && <Text color={'colorPalette.fg/70'} fontSize={'sm'} lineClamp={4}>
                      {founder.shortBio}
                    </Text>}
                  {memberHref && <Button asChild size={'xs'} variant={'outline'}>
                      <NextLink href={memberHref}>View member profile</NextLink>
                    </Button>}
                </VStack>
              </HStack>

              {company?.elevatorPitch && <Box>
                  <Text color={'colorPalette.fg/50'} marginBottom={1} textStyle={'labelXs'}>
                    About {company.name || 'the company'}
                  </Text>
                  <Text fontFamily={'longform'} fontSize={'sm'}>
                    {company.elevatorPitch}
                  </Text>
                </Box>}
            </Card.Body>
          </Card.Root>

          <Card.Root variant={'subtle'}>
            <Card.Body gap={4}>
              <Heading size={'md'}>What they need</Heading>
              {why ? <Text fontFamily={'longform'} fontSize={'md'} whiteSpace={'pre-wrap'}>
                  {why}
                </Text> : <Text color={'colorPalette.fg/60'} fontSize={'sm'}>
                  No description provided for this seat.
                </Text>}

              {skills.length > 0 && <Box>
                  <Text color={'colorPalette.fg/50'} marginBottom={2} textStyle={'labelXs'}>
                    Skills
                  </Text>
                  <Wrap gap={1.5}>
                    {skills.map(s => <SpecialtyTag key={s} colorPalette={category?.palette || 'purple'} label={s} />)}
                  </Wrap>
                </Box>}

              <SimpleGrid columns={{
              base: 1,
              sm: 2
            }} gap={4}>
                {compLabels.length > 0 && <Box>
                    <Text color={'colorPalette.fg/50'} marginBottom={2} textStyle={'labelXs'}>
                      Compensation
                    </Text>
                    <Wrap gap={1.5}>
                      {compLabels.map(l => <Badge key={l} variant={'subtle'}>
                          {l}
                        </Badge>)}
                    </Wrap>
                  </Box>}
                {commitLabels.length > 0 && <Box>
                    <Text color={'colorPalette.fg/50'} marginBottom={2} textStyle={'labelXs'}>
                      Time commitment
                    </Text>
                    <Wrap gap={1.5}>
                      {commitLabels.map(l_0 => <Badge key={l_0} variant={'subtle'}>
                          {l_0}
                        </Badge>)}
                    </Wrap>
                  </Box>}
              </SimpleGrid>
            </Card.Body>
          </Card.Root>

          {match.matchExplanation && <Card.Root variant={'outline'}>
              <Card.Body gap={2}>
                <Heading size={'sm'}>Why you match</Heading>
                <Text fontFamily={'longform'} fontSize={'sm'}>
                  {match.matchExplanation}
                </Text>
              </Card.Body>
            </Card.Root>}

          {factors.length > 0 && <Box>
              <Text color={'colorPalette.fg/50'} marginBottom={2} textStyle={'labelXs'}>
                Match factors
              </Text>
              <Wrap gap={1.5}>
                {factors.map(f => <Badge key={f} colorPalette={category?.palette || 'purple'} variant={'outline'}>
                    {f}
                  </Badge>)}
              </Wrap>
            </Box>}
        </VStack>

        <VStack align={'stretch'} gap={4} position={{
        lg: 'sticky'
      }} top={4}>
          <Card.Root variant={'subtle'}>
            <Card.Body gap={3}>
              <Heading size={'md'}>Actions</Heading>
              <Text color={'colorPalette.fg/60'} fontSize={'sm'}>
                {applicationState === 'pending' ? "We'll notify you when the founder accepts. Chat opens in Inbox after they do." : applicationState === 'accepted' ? 'The founder accepted. Continue in Inbox — this is a board seat, not a paid session.' : applicationState === 'declined' ? 'The founder declined this application. Other seats are still open in Opportunities.' : 'Offer to join this board seat. Chat opens in Inbox after the founder accepts.'}
              </Text>

              {applicationState === 'none' ? <PaywalledAdvisorAction intent={'board-apply'} upgradeCtaButtonProps={{
              width: 'full'
            }} upgradeCtaLabel={'Upgrade to apply'}>
                  <Button variant={'cta'} width={'full'} onClick={() => setShowApplyForm(true)}>
                    <LuSend />
                    Apply for this seat
                  </Button>
                </PaywalledAdvisorAction> : applicationState === 'accepted' && inboxHref ? <Button asChild variant={'cta'} width={'full'}>
                  <NextLink href={inboxHref}>Open Inbox</NextLink>
                </Button> : <Button disabled variant={'cta'} width={'full'}>
                  <LuCheck />
                  {applicationState === 'declined' ? 'Application declined' : 'Application sent'}
                </Button>}

              {applied ? <VStack align={'stretch'} gap={2}>
                  <Button asChild size={'sm'} variant={'outline'} width={'full'}>
                    <NextLink href={paths.opportunities}>Back to opportunities</NextLink>
                  </Button>
                  <Button asChild size={'sm'} variant={'ghost'} width={'full'}>
                    <NextLink href={paths.ownProfile}>Edit profile</NextLink>
                  </Button>
                  <Button asChild size={'sm'} variant={'ghost'} width={'full'}>
                    <NextLink href={paths.sessionSettings}>Session rates</NextLink>
                  </Button>
                </VStack> : null}

              {messageError && <Text color={'red.fg'} fontSize={'sm'}>
                  {messageError}
                </Text>}
            </Card.Body>
          </Card.Root>

          {showApplyForm && !applied && groupRequestId && <Card.Root variant={'outline'}>
              <Card.Body gap={3}>
                <HStack justify={'space-between'}>
                  <Heading size={'sm'}>Apply for this board seat</Heading>
                  <Button size={'xs'} variant={'ghost'} onClick={() => setShowApplyForm(false)}>
                    Cancel
                  </Button>
                </HStack>
                <Text color={'colorPalette.fg/60'} fontSize={'sm'}>
                  The founder will accept or decline. You will not be asked to confirm again.
                </Text>
                <AutoResizeTextarea bg={'colorPalette.subtle'} fontFamily={'longform'} placeholder={'Optional note — why you are a fit for this seat…'} value={applyMessage} onChange={event => setApplyMessage(event.currentTarget.value)} />
                <PaywalledAdvisorAction intent={'board-apply'} upgradeCtaButtonProps={{
              width: 'full'
            }} upgradeCtaLabel={'Upgrade to apply'}>
                  <Button loading={applyMutation.loading} variant={'cta'} onClick={() => void handleApply()}>
                    Send application
                  </Button>
                </PaywalledAdvisorAction>
              </Card.Body>
            </Card.Root>}

          {applicationState === 'pending' && <Text color={'colorPalette.fg/60'} fontSize={'sm'}>
              Application sent — the founder will accept or decline.
            </Text>}
        </VStack>
      </SimpleGrid>

      <Separator />
    </SectionScroll>;
};